import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Controls from "./components/Controls";
import TextInput from "./components/TextInput";
import TextOutput from "./components/TextOutput";
import { processText } from "./engine/pipeline";

const PROFILE_KEY = "humanizer_profile";

async function copyToClipboard(value) {
  if (!value) return false;

  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.top = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    return copied;
  }
}

export default function TextHumanizerPage() {
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [copyLabel, setCopyLabel] = useState("Kopieren");
  const [profile, setProfile] = useState(
    localStorage.getItem(PROFILE_KEY) || "klasse9"
  );

  const canProcess = useMemo(() => Boolean(text.trim()), [text]);

  const handleProcess = () => {
    if (!canProcess) return;
    const processed = processText(text, profile);
    setResult(processed);
  };

  const handleProfileChange = (nextProfile) => {
    setProfile(nextProfile);
    localStorage.setItem(PROFILE_KEY, nextProfile);
  };

  const handleCopy = async () => {
    const copied = await copyToClipboard(result);
    setCopyLabel(copied ? "Kopiert" : "Kopieren fehlgeschlagen");
    window.setTimeout(() => setCopyLabel("Kopieren"), 1600);
  };

  return (
    <div className="min-h-screen bg-[var(--sb-canvas)] text-[var(--sb-ink)]">
      <div className="relative mx-auto w-full max-w-2xl px-4 py-6 sm:px-6 sm:py-10">
        {/* Header / Navigation */}
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="t-display text-3xl">
              Text natürlicher machen
            </h1>
            <p className="t-caption mt-1">
              Das neue Tool von Schriftbot
            </p>
          </div>

          <Link
            to="/"
            className="sb-link shrink-0 t-label"
          >
            Zurück
          </Link>
        </header>

        <main className="mt-6 flex flex-col gap-4">
          {/* INPUT */}
          <section className="sb-card p-5 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="t-label">
                Eingabe
              </div>
              <div className="t-caption">
                {text.length}
              </div>
            </div>

            <div className="mt-3">
              <TextInput value={text} onChange={setText} />
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={handleProcess}
                disabled={!canProcess}
                className="sb-btn sb-btn-secondary w-full"
              >
                Verarbeiten
              </button>
            </div>
          </section>

          {/* OUTPUT */}
          <section className="sb-card p-5 sm:p-6">
            <div className="t-label">
              Ausgabe
            </div>
            <div className="mt-3">
              <TextOutput
                value={result}
                onCopy={handleCopy}
                copyLabel={copyLabel}
              />
            </div>
          </section>

          {/* COPY */}
          <footer className="pt-2">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!result}
              className="sb-btn sb-btn-primary w-full"
            >
              {copyLabel}
            </button>

            <div className="t-caption mt-4 text-center">
              Support: schriftbot@gmail.com
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
